import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Boxes, RotateCcw, Compass, Sparkles } from "lucide-react";
import DrillholeViewer3D from "@/components/DrillholeViewer3D";
import BlockEditDialog from "@/components/BlockEditDialog";
import { useDesktop } from "@/lib/desktop-context";
import VariogramFitDialog from "@/components/VariogramFitDialog";
import GradeTonnagePanel from "@/components/GradeTonnagePanel";
import { readOreFromBm, readWasteFromBm, SsotOreBlocks, SsotOreGrade, OreTonnage, formatTonnage } from "@/components/ssot/OreFigures";
import SearchEllipsoidPanel from "@/components/estimation/SearchEllipsoidPanel";
import DensityClassPanel from "@/components/DensityClassPanel";
import SearchMultiplierSlider from "@/components/SearchMultiplierSlider";
import { BLOCKMODEL } from "@/constants/testIds";
import { toast } from "sonner";
import AutoFilledHint from "@/components/AutoFilledHint";

export default function BlockModelTab({ projectId, onChange }) {
  const [models, setModels] = useState([]);
  const [holes, setHoles] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("idw");
  const [showEllipsoid, setShowEllipsoid] = useState(false);
  const [blockLoad, setBlockLoad] = useState({ status: "idle", loaded: 0, total: 0 });
  const [searchMult, setSearchMult] = useState(2.0);
  const [form, setForm] = useState({
    name: "Estimate v1",
    block_size: 15,
    cutoff_grade: 0.3,
    nx: 12, ny: 12, nz: 6,
    origin_x: -80, origin_y: -80, origin_z: 105,
    base_grade: 0.5,
    variogram_range: 60,
    variogram_sill: 0.5,
    variogram_nugget: 0.05,
    variogram_azimuth: 0,
    variogram_dip: 0,
    variogram_anisotropy: 1.0,
  });
  // Sprint B · Feb 2026 — full-3D search ellipsoid + per-domain filter
  const [searchEllipsoid, setSearchEllipsoid] = useState({
    range_major: 60, range_semi_major: 40, range_minor: 20,
    azimuth: 0, dip: 0, plunge: 0,
  });
  const [domainList, setDomainList] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [ellipsoidExpanded, setEllipsoidExpanded] = useState(false);
  // Sprint C · Feb 2026 — Surpac/Vulcan rotation + sub-blocking
  const [rotation, setRotation] = useState({ azimuth: 0, dip: 0, plunge: 0 });
  const [subblock, setSubblock] = useState({ enabled: false, factor: 2 });
  const [surpacExpanded, setSurpacExpanded] = useState(false);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/estimation/domain-list`)
      .then((r) => setDomainList(r.data?.domains || []))
      .catch(() => setDomainList([]));
  }, [projectId]);
  // Iter I-15 · one-click auto-fetch from uploaded Collar/Assay data
  const [autoFilled, setAutoFilled] = useState({});   // fieldName → {value, reason, source}
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoEligible, setAutoEligible] = useState(true);
  const [autoEligibilityReason, setAutoEligibilityReason] = useState("");

  const fetchAutoFill = async () => {
    setAutoBusy(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/block-model-auto-fill`);
      if (data.eligible === false) {
        setAutoEligible(false);
        setAutoEligibilityReason(data.reason || "");
        toast.error(data.reason || "Not enough data to auto-fetch.");
        return;
      }
      setAutoEligible(true);
      const f = data.fields || {};
      setAutoFilled(f);
      // Update the form with derived values in one shot, without touching
      // the Name or Estimation Method (per user spec).
      setForm((prev) => ({
        ...prev,
        block_size: f.block_size?.value ?? prev.block_size,
        cutoff_grade: f.cutoff_grade?.value ?? prev.cutoff_grade,
        base_grade: f.base_grade?.value ?? prev.base_grade,
        nx: f.nx?.value ?? prev.nx,
        ny: f.ny?.value ?? prev.ny,
        nz: f.nz?.value ?? prev.nz,
        origin_x: f.origin_x?.value ?? prev.origin_x,
        origin_y: f.origin_y?.value ?? prev.origin_y,
        origin_z: f.origin_z?.value ?? prev.origin_z,
      }));
      toast.success("Auto-fetched — review the highlighted fields and Generate when ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fetch failed");
    } finally {
      setAutoBusy(false);
    }
  };

  // Preflight eligibility check when the modal opens — disables the button
  // (with tooltip) if the project has no Collar/Assay data yet.
  useEffect(() => {
    if (!open || !projectId) return;
    api.get(`/projects/${projectId}/block-model-auto-fill`)
      .then(({ data }) => {
        setAutoEligible(!!data.eligible);
        setAutoEligibilityReason(data.reason || "");
      })
      .catch(() => setAutoEligible(false));
  }, [open, projectId]);

  const load = () => {
    api.get(`/projects/${projectId}/block-models`).then((r) => {
      setModels(r.data);
      if (r.data.length && !active) setActive(r.data[0]);
      if (!r.data.length) setActive(null);
    });
    api.get(`/projects/${projectId}/drillholes`).then((r) => setHoles(r.data));
  };

  useEffect(() => { load(); }, [projectId]);

  // Adaptive stride (client-side LOD) — huge deposits fetch every Nth block
  // for a snappy preview. Users can flip to "Full res" to fetch every block
  // (still capped by the backend at 2M for safety).
  const [fullResolution, setFullResolution] = useState(false);
  const [effectiveStride, setEffectiveStride] = useState(1);
  // In-viewer block editing state
  const [editingBlock, setEditingBlock] = useState(null); // { block, index }
  const desktop = useDesktop();

  const pickStride = (total) => {
    // Levels tuned so any of these fetches finishes in <10 s over the pod's
    // 3 Gbit ingress. The 3D viewer's own instance limit will further clamp
    // rendering after arrival.
    if (!total || total <= 50_000)     return 1;
    if (total <= 500_000)              return 5;
    if (total <= 2_000_000)            return 25;
    return 100;
  };

  // Progressive fetch of blocks for the active model via the NDJSON stream.
  // Keeps the 3D viewer responsive on large deposits — chunks arrive as they
  // stream from the server and are merged into `active.blocks` in place.
  useEffect(() => {
    if (!active?.id) return;
    let cancelled = false;
    const activeId = active.id;
    // Session I-4 fix — reset the block buffer to empty before re-fetching.
    // Previously, toggling `fullResolution` re-ran this effect WITHOUT
    // clearing `active.blocks`, so blocks accumulated as duplicate
    // instances at identical coordinates. The user reported "Full Res on"
    // renders a smooth gradient hull — that was 2×/3× stacked cubes at
    // identical positions, not a hull mesh. Now we clear first, then
    // stream fresh.
    setActive((cur) => (cur && cur.id === activeId ? { ...cur, blocks: [] } : cur));
    (async () => {
      setBlockLoad({ status: "loading", loaded: 0, total: active.blocks_count ?? 0 });
      // Total (respecting any global cap the server enforces)
      let total = active.blocks_count;
      try {
        const cr = await api.get(`/projects/${projectId}/block-models/${activeId}/blocks/count`);
        total = cr.data.count;
      } catch (err) {
        // Non-fatal — total is cosmetic; use active.blocks_count fallback
        console.debug("[block-model] count endpoint failed:", err?.message);
      }
      if (cancelled) return;
      const stride = fullResolution ? 1 : pickStride(total);
      setEffectiveStride(stride);
      setBlockLoad((s) => ({ ...s, total, stride }));
      try {
        const base = api.defaults.baseURL.replace(/\/$/, "");
        const url = `${base}/projects/${projectId}/block-models/${activeId}/blocks`
          + `?chunk_size=5000&limit=200000&stride=${stride}`;
        const token = localStorage.getItem("smin_token") || localStorage.getItem("stope_token");
        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok || !res.body) throw new Error(`stream failed: ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        const chunkBlocks = [];
        let loaded = 0;
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          if (cancelled) { reader.cancel(); return; }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              chunkBlocks.push(JSON.parse(line));
              loaded += 1;
            } catch (err) {
              // Skip malformed NDJSON line — stream continues
              if (process.env.NODE_ENV !== "production") {
                console.debug("[block-model] skip malformed block line:", err?.message);
              }
            }
          }
          // Iter I-17 perf — flush every 20k blocks (was 5k). Cuts React
          // + Three.js re-renders 4× on a 200k model without hurting
          // perceived progress (5k blocks fill the viewport frustum
          // faster than the eye can track anyway).
          if (chunkBlocks.length >= 20000) {
            const snapshot = chunkBlocks.splice(0, chunkBlocks.length);
            setActive((cur) => {
              if (!cur || cur.id !== activeId) return cur;
              return { ...cur, blocks: [...(cur.blocks || []), ...snapshot] };
            });
            setBlockLoad({ status: "loading", loaded, total });
          }
        }
        if (buffer.trim()) {
          try { chunkBlocks.push(JSON.parse(buffer)); loaded += 1; }
          catch (err) {
            if (process.env.NODE_ENV !== "production") {
              console.debug("[block-model] skip trailing malformed block:", err?.message);
            }
          }
        }
        if (chunkBlocks.length) {
          setActive((cur) => {
            if (!cur || cur.id !== activeId) return cur;
            return { ...cur, blocks: [...(cur.blocks || []), ...chunkBlocks] };
          });
        }
        setBlockLoad({ status: "done", loaded, total });
        // V4.0 Sprint 4 · publish loaded-block count to the Performance Monitor
        try { window.__smin_blocks_loaded = loaded; } catch (_ignore) { /* window not writable */ }
      } catch (e) {
        if (!cancelled) {
          setBlockLoad({ status: "error", loaded: 0, total });
          toast.error("Failed to load block data");
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, projectId, fullResolution]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = method === "kriging"
        ? `/projects/${projectId}/block-models/kriging`
        : `/projects/${projectId}/block-models`;
      const commonSurpac = {
        rotation_azimuth:     Number(rotation.azimuth) || 0,
        rotation_dip:         Number(rotation.dip)     || 0,
        rotation_plunge:      Number(rotation.plunge)  || 0,
        subblock_at_boundary: !!subblock.enabled,
        subblock_factor:      Math.max(1, Math.min(4, Number(subblock.factor) || 2)),
      };
      const payload = method === "kriging"
        ? {
            name: form.name,
            origin: [form.origin_x, form.origin_y, form.origin_z],
            block_size: form.block_size,
            dimensions: [form.nx, form.ny, form.nz],
            cutoff_grade: form.cutoff_grade,
            variogram_range: form.variogram_range,
            variogram_sill: form.variogram_sill,
            variogram_nugget: form.variogram_nugget,
            variogram_azimuth: form.variogram_azimuth,
            variogram_dip: form.variogram_dip,
            variogram_anisotropy: form.variogram_anisotropy,
            // Sprint B — full 3D search ellipsoid + optional lithology hard boundary
            search_ellipsoid: searchEllipsoid,
            search_domain: selectedDomain || null,
            ...commonSurpac,
          }
        : {
            name: form.name,
            origin: [form.origin_x, form.origin_y, form.origin_z],
            block_size: form.block_size,
            dimensions: [form.nx, form.ny, form.nz],
            cutoff_grade: form.cutoff_grade,
            base_grade: form.base_grade,
            search_ellipsoid: searchEllipsoid,
            search_domain: selectedDomain || null,
            ...commonSurpac,
          };
      const { data } = await api.post(path, payload);
      toast.success(`${method === "kriging" ? "Kriging" : "IDW"} model generated`);
      setOpen(false);
      setActive(data);
      load();
      onChange?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      // Enterprise Kriging validation returns a structured error with issues[]
      if (d && typeof d === "object" && Array.isArray(d.issues)) {
        toast.error(d.message || "Validation failed", {
          description: (
            <ul className="mt-2 space-y-1 text-xs">
              {d.issues.slice(0, 5).map((iss, i) => (
                <li key={i}>
                  <span className="font-mono text-primary">{iss.field}</span> — {iss.message}
                </li>
              ))}
              {d.issues.length > 5 && (
                <li className="opacity-60 italic">+ {d.issues.length - 5} more…</li>
              )}
            </ul>
          ),
          duration: 12000,
        });
      } else if (Array.isArray(d)) {
        // Pydantic schema errors
        toast.error(d.map((e) => `${e.loc?.slice(-1)?.[0]}: ${e.msg}`).join(" · "));
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Failed"));
      }
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this block model?")) return;
    await api.delete(`/projects/${projectId}/block-models/${id}`);
    if (active?.id === id) setActive(null);
    load();
    onChange?.();
  };

  const recall = (m) => {
    const vc = m?.summary?.variogram_config;
    const isKriging = m?.summary?.method === "kriging";
    setMethod(isKriging ? "kriging" : "idw");
    setForm((prev) => ({
      ...prev,
      name: `${m.name} · copy`,
      block_size: m.block_size ?? prev.block_size,
      cutoff_grade: m.cutoff_grade ?? prev.cutoff_grade,
      nx: m.dimensions?.[0] ?? prev.nx,
      ny: m.dimensions?.[1] ?? prev.ny,
      nz: m.dimensions?.[2] ?? prev.nz,
      origin_x: m.origin?.[0] ?? prev.origin_x,
      origin_y: m.origin?.[1] ?? prev.origin_y,
      origin_z: m.origin?.[2] ?? prev.origin_z,
      variogram_range: vc?.range ?? prev.variogram_range,
      variogram_sill: vc?.sill ?? prev.variogram_sill,
      variogram_nugget: vc?.nugget ?? prev.variogram_nugget,
      variogram_azimuth: vc?.azimuth ?? prev.variogram_azimuth,
      variogram_dip: vc?.dip ?? prev.variogram_dip,
      variogram_anisotropy: vc?.anisotropy ?? prev.variogram_anisotropy,
    }));
    setOpen(true);
    toast.success(`Recalled settings from "${m.name}"`);
  };

  return (
    <div className="h-[calc(100vh-8.75rem)] grid grid-cols-1 lg:grid-cols-[320px_1fr]">
      <aside className="border-r border-border bg-card p-5 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="smin-label">Block models</div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm"
                data-testid={BLOCKMODEL.createButton}
                className="rounded-none h-7 font-mono text-[10px] uppercase tracking-widest">
                <Plus className="h-3 w-3 mr-1" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-border bg-card sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">Generate block model</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Choose the estimation method and grid parameters. Kriging uses a spherical variogram.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-3">
                {/* Iter I-15 · one-click auto-fetch from uploaded data */}
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline"
                    onClick={fetchAutoFill}
                    disabled={autoBusy || !autoEligible}
                    title={autoEligible ? "" : (autoEligibilityReason || "No Collar/Assay data uploaded yet.")}
                    data-testid="blockmodel-auto-fetch-btn"
                    className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest border-primary/40 text-primary hover:bg-primary/10">
                    <Sparkles className="h-3 w-3 mr-1.5" />
                    {autoBusy ? "Fetching…" : "Auto-fetch from loaded data"}
                  </Button>
                  {!autoEligible && (
                    <span className="text-[10px] text-muted-foreground italic"
                      data-testid="blockmodel-auto-fetch-disabled-reason">
                      {autoEligibilityReason || "No Collar/Assay data yet."}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 border border-border p-1 w-fit">
                  {[
                    { v: "idw", l: "IDW (fast)" },
                    { v: "kriging", l: "Ordinary Kriging" },
                  ].map((o) => (
                    <button key={o.v} type="button" onClick={() => setMethod(o.v)}
                      data-testid={`blockmodel-method-${o.v}`}
                      className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${
                        method === o.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                      }`}>
                      {o.l}
                    </button>
                  ))}
                </div>
                <div>
                  <Label className="smin-label">Name</Label>
                  <Input required value={form.name} data-testid={BLOCKMODEL.nameInput}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none bg-input font-mono" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="smin-label">Block size</Label>
                    <Input type="number" step="0.5" value={form.block_size} data-testid={BLOCKMODEL.blockSizeInput}
                      onChange={(e) => setForm({ ...form, block_size: parseFloat(e.target.value) })}
                      className="rounded-none bg-input font-mono" />
                    {autoFilled.block_size && (
                      <AutoFilledHint reason={autoFilled.block_size.reason} source={autoFilled.block_size.source} compact />
                    )}
                  </div>
                  <div>
                    <Label className="smin-label">Cutoff g</Label>
                    <Input type="number" step="0.01" value={form.cutoff_grade} data-testid={BLOCKMODEL.cutoffInput}
                      onChange={(e) => setForm({ ...form, cutoff_grade: parseFloat(e.target.value) })}
                      className="rounded-none bg-input font-mono" />
                    {autoFilled.cutoff_grade && (
                      <AutoFilledHint reason={autoFilled.cutoff_grade.reason} source={autoFilled.cutoff_grade.source} compact />
                    )}
                  </div>
                  {method === "idw" ? (
                    <div>
                      <Label className="smin-label">Base grade</Label>
                      <Input type="number" step="0.01" value={form.base_grade}
                        onChange={(e) => setForm({ ...form, base_grade: parseFloat(e.target.value) })}
                        className="rounded-none bg-input font-mono" />
                      {autoFilled.base_grade && (
                        <AutoFilledHint reason={autoFilled.base_grade.reason} source={autoFilled.base_grade.source} compact />
                      )}
                    </div>
                  ) : (
                    <div>
                      <Label className="smin-label">Range</Label>
                      <Input type="number" step="1" value={form.variogram_range}
                        onChange={(e) => setForm({ ...form, variogram_range: parseFloat(e.target.value) })}
                        className="rounded-none bg-input font-mono" />
                    </div>
                  )}
                  {["nx", "ny", "nz"].map((k) => (
                    <div key={k}>
                      <Label className="smin-label">{k.toUpperCase()}</Label>
                      <Input type="number" value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: parseInt(e.target.value) })}
                        className="rounded-none bg-input font-mono" />
                      {autoFilled[k] && (
                        <AutoFilledHint reason={autoFilled[k].reason} source={autoFilled[k].source} compact />
                      )}
                    </div>
                  ))}
                  {["origin_x", "origin_y", "origin_z"].map((k) => (
                    <div key={k}>
                      <Label className="smin-label">{k.replace("origin_", "O.")}</Label>
                      <Input type="number" value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: parseFloat(e.target.value) })}
                        className="rounded-none bg-input font-mono" />
                      {autoFilled[k] && (
                        <AutoFilledHint reason={autoFilled[k].reason} source={autoFilled[k].source} compact />
                      )}
                    </div>
                  ))}
                </div>
                {method === "kriging" && (
                  <div className="border-t border-border pt-3 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="smin-label">Azimuth °</Label>
                        <Input type="number" step="1" value={form.variogram_azimuth}
                          data-testid="blockmodel-variogram-azimuth-input"
                          onChange={(e) => setForm({ ...form, variogram_azimuth: parseFloat(e.target.value) })}
                          className="rounded-none bg-input font-mono" />
                      </div>
                      <div>
                        <Label className="smin-label">Dip °</Label>
                        <Input type="number" step="1" value={form.variogram_dip}
                          data-testid="blockmodel-variogram-dip-input"
                          onChange={(e) => setForm({ ...form, variogram_dip: parseFloat(e.target.value) })}
                          className="rounded-none bg-input font-mono" />
                      </div>
                      <div>
                        <Label className="smin-label">Anisotropy</Label>
                        <Input type="number" step="0.05" value={form.variogram_anisotropy}
                          data-testid="blockmodel-variogram-anisotropy-input"
                          onChange={(e) => setForm({ ...form, variogram_anisotropy: parseFloat(e.target.value) })}
                          className="rounded-none bg-input font-mono" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[10px] text-muted-foreground">
                        R {form.variogram_range} · S {form.variogram_sill} · N {form.variogram_nugget}
                        <span className="ml-2">· Az {form.variogram_azimuth}° · Dp {form.variogram_dip}° · An {form.variogram_anisotropy}</span>
                      </div>
                      <VariogramFitDialog
                        projectId={projectId}
                        initialParams={form}
                        onApply={(p) => setForm({ ...form, ...p })}
                      />
                    </div>
                  </div>
                )}

                {/* Sprint B · Feb 2026 — full-3D search ellipsoid + per-domain filter */}
                <div className="border-t border-border pt-3">
                  <button
                    type="button"
                    onClick={() => setEllipsoidExpanded((v) => !v)}
                    data-testid="ellipsoid-toggle"
                    className="w-full flex items-center justify-between text-left px-2 py-2 hover:bg-accent/40"
                  >
                    <span className="text-[11px] font-mono uppercase tracking-widest text-neutral-700">
                      Advanced · Search ellipsoid & domain filter
                    </span>
                    <span className="text-[10px] font-mono text-neutral-500">
                      {ellipsoidExpanded ? "▲ hide" : "▼ configure"}
                      {selectedDomain ? ` · domain: ${selectedDomain}` : ""}
                    </span>
                  </button>
                  {ellipsoidExpanded && (
                    <div className="mt-2">
                      <SearchEllipsoidPanel
                        projectId={projectId}
                        value={searchEllipsoid}
                        onChange={setSearchEllipsoid}
                        domains={domainList}
                        selectedDomain={selectedDomain}
                        onDomainChange={setSelectedDomain}
                      />
                    </div>
                  )}
                </div>

                {/* Sprint C · Feb 2026 — Surpac/Vulcan rotation + sub-blocking */}
                <div className="border-t border-border pt-3">
                  <button
                    type="button"
                    onClick={() => setSurpacExpanded((v) => !v)}
                    data-testid="surpac-toggle"
                    className="w-full flex items-center justify-between text-left px-2 py-2 hover:bg-accent/40"
                  >
                    <span className="text-[11px] font-mono uppercase tracking-widest text-neutral-700">
                      Advanced · Rotated block model & sub-blocking (Surpac / Vulcan)
                    </span>
                    <span className="text-[10px] font-mono text-neutral-500">
                      {surpacExpanded ? "▲ hide" : "▼ configure"}
                      {(rotation.azimuth || rotation.dip || rotation.plunge) ?
                        ` · rot Az ${rotation.azimuth}° Dp ${rotation.dip}° Pl ${rotation.plunge}°` : ""}
                      {subblock.enabled ? ` · sub-block ${subblock.factor}³` : ""}
                    </span>
                  </button>
                  {surpacExpanded && (
                    <div className="mt-2 border border-border bg-card p-4 space-y-4"
                         data-testid="surpac-panel">
                      <div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-2">
                          Grid rotation
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="smin-label">Azimuth °</Label>
                            <Input type="number" step="1" value={rotation.azimuth}
                              data-testid="rotation-azimuth-input"
                              onChange={(e) => setRotation({ ...rotation, azimuth: parseFloat(e.target.value) || 0 })}
                              className="rounded-none bg-input font-mono" />
                          </div>
                          <div>
                            <Label className="smin-label">Dip °</Label>
                            <Input type="number" step="1" value={rotation.dip}
                              data-testid="rotation-dip-input"
                              onChange={(e) => setRotation({ ...rotation, dip: parseFloat(e.target.value) || 0 })}
                              className="rounded-none bg-input font-mono" />
                          </div>
                          <div>
                            <Label className="smin-label">Plunge °</Label>
                            <Input type="number" step="1" value={rotation.plunge}
                              data-testid="rotation-plunge-input"
                              onChange={(e) => setRotation({ ...rotation, plunge: parseFloat(e.target.value) || 0 })}
                              className="rounded-none bg-input font-mono" />
                          </div>
                        </div>
                          <p className="text-[10px] text-neutral-500 mt-2">
                            Rotates the block grid about the origin. Use when the ore body&rsquo;s
                            strike &amp; dip don&rsquo;t align with world axes.
                          </p>
                      </div>
                      <div className="border-t border-border pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">
                            Sub-blocking at grade-shell boundary
                          </div>
                          <label className="flex items-center gap-2 text-[11px] font-mono cursor-pointer">
                            <input
                              type="checkbox"
                              checked={subblock.enabled}
                              onChange={(e) => setSubblock({ ...subblock, enabled: e.target.checked })}
                              data-testid="subblock-enabled-checkbox"
                              className="h-3.5 w-3.5"
                            />
                            Enable
                          </label>
                        </div>
                        <div className="grid grid-cols-3 gap-3 items-end">
                          <div>
                            <Label className="smin-label">Factor</Label>
                            <select
                              value={subblock.factor}
                              disabled={!subblock.enabled}
                              onChange={(e) => setSubblock({ ...subblock, factor: parseInt(e.target.value, 10) })}
                              data-testid="subblock-factor-select"
                              className="w-full h-10 rounded-none border border-neutral-300 bg-input text-xs font-mono px-2 disabled:opacity-40"
                            >
                              <option value={2}>2³ (8 children)</option>
                              <option value={3}>3³ (27 children)</option>
                              <option value={4}>4³ (64 children)</option>
                            </select>
                          </div>
                          <p className="col-span-2 text-[10px] text-neutral-500 leading-tight">
                            Boundary blocks (grade within ±20 % of the cutoff) are subdivided
                            into <span className="font-mono text-neutral-800">factor³</span>{" "}
                            children for high-resolution edge definition. Interior blocks stay
                            at parent size so compute stays cheap.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <Button type="submit" disabled={busy} data-testid={BLOCKMODEL.submitButton}
                  className="rounded-none w-full font-heading">
                  {busy ? "Estimating…" : "Generate"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        {models.length === 0 && (
          <div className="text-sm text-muted-foreground">No block models yet.</div>
        )}
        <div className="space-y-2">
          {models.map((m) => (
            <div key={m.id} data-testid={BLOCKMODEL.card}
              onClick={() => setActive(m)}
              role="button" tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" ? setActive(m) : null)}
              className={`cursor-pointer w-full text-left border p-3 transition-colors ${
                active?.id === m.id ? "border-primary bg-accent" : "border-border hover:bg-accent/50"
              }`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-heading font-bold truncate">{m.name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground mt-1">
                    {m.dimensions.join("×")} · {m.block_size}m blocks
                  </div>
                  {m.summary?.method && (
                    <div className="font-mono text-[10px] mt-1">
                      <span className={`px-1.5 py-0.5 border ${
                        m.summary.method === "kriging"
                          ? "border-primary/60 text-primary"
                          : "border-border text-muted-foreground"
                      }`}>
                        {m.summary.method === "kriging" ? "KRIGING" : "IDW"}
                      </span>
                      {m.summary?.variogram_config && (
                        <span className="ml-2 text-muted-foreground">
                          R{m.summary.variogram_config.range} · S{m.summary.variogram_config.sill} · N{m.summary.variogram_config.nugget}
                          {(m.summary.variogram_config.azimuth || m.summary.variogram_config.dip || (m.summary.variogram_config.anisotropy && m.summary.variogram_config.anisotropy !== 1)) && (
                            <span className="ml-1">
                              · Az{m.summary.variogram_config.azimuth || 0}° Dp{m.summary.variogram_config.dip || 0}° An{m.summary.variogram_config.anisotropy || 1}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={(e) => { e.stopPropagation(); recall(m); }}
                    data-testid={BLOCKMODEL.recallButton}
                    title="Recall these settings into the New dialog"
                    className="p-1 hover:text-primary">
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); del(m.id); }}
                    data-testid={BLOCKMODEL.deleteButton}
                    className="p-1 hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {m.summary && (
                <div className="grid grid-cols-2 gap-1 mt-2 font-mono text-[10px]">
                  <div>
                    <span className="smin-label">Ore</span>{" "}
                    <SsotOreBlocks bm={m} className="text-primary" data-testid={`bm-card-ore-${m.id}`} />
                  </div>
                  <div>
                    <span className="smin-label">Avg ore g</span>{" "}
                    <SsotOreGrade bm={m} decimals={4} className="text-secondary" data-testid={`bm-card-oregrade-${m.id}`} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {active?.summary && (
          <div className="mt-6 border-t border-border pt-4">
            <div className="smin-label mb-3">Summary</div>
            <div className="space-y-2 font-mono text-xs">
              {(() => {
                // Session I-3 retrofit — read the canonical ore/waste split
                // through the shared `readOreFromBm` helper so this panel
                // and every other SSoT consumer (mini-card, G-T readout,
                // Dashboard, Reports) share one lookup implementation.
                // Overburden is a SUB-line inside Waste per Master Spec §1.
                const oreR   = readOreFromBm(active);
                const wasteR = readWasteFromBm(active);
                const cs     = active.class_summary || {};
                const by     = cs.by_kind || null;
                const overburdenBlocks = cs.overburden_blocks ?? by?.overburden?.blocks ?? null;
                const overburdenTonn   = cs.total_overburden_tonnage ?? by?.overburden?.tonnage ?? null;
                const strip  = oreR.hasSsot
                  ? (cs.strip_ratio ?? by?.strip_ratio ?? 0)
                  : (active.summary.strip_ratio ?? 0);
                const rows = [
                  ["Total blocks", active.summary.total_blocks],
                  ["Ore blocks", oreR.blocks],
                  ["Waste blocks (incl. overburden)", wasteR.blocks],
                  ...(overburdenBlocks != null ? [
                    ["↳ Overburden blocks", overburdenBlocks],
                  ] : []),
                  ["Ore tonnage",   formatTonnage(oreR.tonnage ?? 0, "auto", { decimals: 2 })],
                  ["Waste tonnage (incl. overburden)",
                                    formatTonnage(wasteR.tonnage ?? 0, "auto", { decimals: 2 })],
                  ...(overburdenTonn != null ? [
                    ["↳ Overburden tonnage",
                                    formatTonnage(overburdenTonn, "auto", { decimals: 2 })],
                    ["Overburden ratio",       cs.overburden_ratio ?? 0],
                  ] : []),
                  ["Total tonnage (Ore + Waste)",
                                    formatTonnage((oreR.tonnage ?? 0) + (wasteR.tonnage ?? 0),
                                                  "auto", { decimals: 2 })],
                  ["Strip ratio (W/O)", strip],
                  ["Avg ore grade",   oreR.avgGrade ?? active.summary.avg_grade],
                  ["Cutoff",          active.summary.cutoff_grade],
                  // Sprint C · Feb 2026 — Surpac/Vulcan fingerprints
                  ...(active.summary.is_rotated ? [
                    ["Grid rotation",
                     `Az ${active.summary.rotation_azimuth}° · Dp ${active.summary.rotation_dip}° · Pl ${active.summary.rotation_plunge}°`],
                  ] : []),
                  ...(active.summary.subblock_applied ? [
                    ["Sub-blocking",
                     `${active.summary.subblock_factor}³ at boundary · ${active.summary.subblock_count ?? 0} sub-blocks`],
                  ] : []),
                  // Sprint C — grade-shell volume from the ore-shell endpoint, if
                  // the operator saved the shell metrics onto the model summary
                  // (see /geomodel/ore-shell live-preview payload).
                  ...(active.summary.grade_shell_volume_m3 != null ? [
                    ["Grade-shell volume",
                     active.summary.grade_shell_volume_m3 >= 1_000_000
                       ? `${(active.summary.grade_shell_volume_m3 / 1_000_000).toFixed(2)} Mm³`
                       : `${Number(active.summary.grade_shell_volume_m3).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} m³`],
                    ["Grade-shell tonnage",
                     formatTonnage(active.summary.grade_shell_tonnage ?? 0, "auto", { decimals: 2 })],
                  ] : []),
                ];
                return rows;
              })().map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border pb-1">
                  <span className="text-muted-foreground">{k}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
            {blockLoad.status === "loading" && (
              <div className="mt-3 border border-primary/40 bg-primary/5 p-2"
                   data-testid="blockmodel-load-progress">
                <div className="flex items-center justify-between font-mono text-[10px] text-primary">
                  <span>STREAMING BLOCKS · stride {effectiveStride}×</span>
                  <span>{blockLoad.loaded.toLocaleString()}
                    {blockLoad.total ? ` / ${blockLoad.total.toLocaleString()}` : ""}</span>
                </div>
                <div className="h-1 mt-1.5 bg-primary/10">
                  <div className="h-full bg-primary transition-all"
                       style={{ width: blockLoad.total
                         ? `${Math.min(100, (blockLoad.loaded / blockLoad.total * effectiveStride) * 100)}%`
                         : "50%" }} />
                </div>
              </div>
            )}
            {blockLoad.status === "done" && (
              <div className="mt-3 border border-border p-2 flex items-center justify-between font-mono text-[10px]"
                   data-testid="blockmodel-lod-toolbar">
                <div>
                  <div className="text-muted-foreground uppercase tracking-widest">LOD</div>
                  <div className="text-primary">
                    stride {effectiveStride}× · {blockLoad.loaded.toLocaleString()} of {(blockLoad.total || 0).toLocaleString()} blocks rendered
                  </div>
                </div>
                <label className="inline-flex items-center gap-1.5 cursor-pointer"
                       title="Fetch every block instead of the adaptive-stride preview">
                  <input type="checkbox"
                         checked={fullResolution}
                         onChange={(e) => setFullResolution(e.target.checked)}
                         data-testid="blockmodel-full-resolution-toggle"
                         className="accent-primary" />
                  <span className="uppercase tracking-widest">Full res</span>
                </label>
              </div>
            )}
            {blockLoad.status === "done" && blockLoad.total > 200000 && effectiveStride === 1 && (
              <div className="mt-3 font-mono text-[10px] text-muted-foreground"
                   data-testid="blockmodel-load-cap">
                Displayed {blockLoad.loaded.toLocaleString()} / {blockLoad.total.toLocaleString()} blocks
                (viewer cap for browser memory)
              </div>
            )}
            <div className="mt-4">
              <GradeTonnagePanel
                projectId={projectId}
                refreshKey={active?.id}
                ssotOre={readOreFromBm(active).tonnage}
              />
            </div>
            <DensityClassPanel projectId={projectId} refreshKey={active?.id} />
            <div className="mt-4">
              <SearchMultiplierSlider
                projectId={projectId}
                value={searchMult}
                onChange={setSearchMult}
                compact
              />
            </div>
          </div>
        )}
      </aside>

      <div className="relative min-h-[400px]">
        {active ? (
          <>
            <DrillholeViewer3D
              drillholes={holes}
              mode="grade"
              blockModel={active}
              cutoff={active.cutoff_grade}
              ellipsoid={showEllipsoid ? buildEllipsoid(active) : null}
              height="100%"
              onBlockClick={(block, index, ev) => {
                // Left click = select (push to Property Inspector via desktop selection)
                // Right click = edit dialog
                if (ev && ev.button === 2) {
                  setEditingBlock({ block, index });
                } else {
                  desktop?.selectObject?.({
                    kind: "block",
                    id: block.id || block._id || `block-${index}`,
                    name: `Block @ (${block.x?.toFixed?.(0)}, ${block.y?.toFixed?.(0)}, ${block.z?.toFixed?.(0)})`,
                    type: "Block",
                    groupKind: "block-model",
                    projectId,
                    raw: block,
                  });
                }
              }}
              highlightBlockIndex={editingBlock?.index ?? -1}
            />
            {active?.summary?.method === "kriging" && active?.summary?.variogram_config && (
              <button
                onClick={() => setShowEllipsoid((v) => !v)}
                data-testid="blockmodel-toggle-ellipsoid"
                className={`absolute top-3 right-3 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest transition-colors flex items-center gap-1.5 ${
                  showEllipsoid
                    ? "bg-primary/20 border-primary text-primary"
                    : "bg-card/90 border-border text-muted-foreground hover:text-primary"
                }`}
                title="Toggle anisotropy ellipsoid"
              >
                <Compass className="h-3 w-3" />
                Ellipsoid
              </button>
            )}
          </>
        ) : (
          <div className="h-full grid place-items-center">
            <div className="text-center max-w-sm">
              <Boxes className="h-8 w-8 text-muted-foreground mx-auto" />
              <div className="smin-label mt-3">No estimate</div>
              <div className="text-sm text-muted-foreground mt-1">Create a block model to visualize ore blocks in 3D.</div>
            </div>
          </div>
        )}
      </div>
      <BlockEditDialog
        open={!!editingBlock}
        block={editingBlock?.block}
        projectId={projectId}
        blockModelId={active?.id}
        onClose={() => setEditingBlock(null)}
        onSaved={(patched) => {
          // Update in place so the viewer reflects the change immediately
          if (!active) return;
          const idx = editingBlock?.index;
          if (idx == null) return;
          const newBlocks = [...(active.blocks || [])];
          newBlocks[idx] = { ...newBlocks[idx], ...patched };
          setActive({ ...active, blocks: newBlocks });
        }}
      />
    </div>
  );
}

/**
 * Derive a variogram search ellipsoid from a kriging block model. Center is
 * the centroid of ore blocks (fall back to model origin). Ranges + orientation
 * come from summary.variogram_config.
 */
function buildEllipsoid(bm) {
  const vc = bm?.summary?.variogram_config;
  if (!vc) return null;
  const blocks = (bm.blocks || []).filter((b) => b.rock_type === "ore");
  let cx, cy, cz;
  if (blocks.length) {
    let sx = 0, sy = 0, sz = 0;
    blocks.forEach((b) => { sx += b.x; sy += b.y; sz += b.z; });
    cx = sx / blocks.length; cy = sy / blocks.length; cz = sz / blocks.length;
  } else {
    const o = bm.origin || [0, 0, 0];
    const d = bm.dimensions || [1, 1, 1];
    const s = bm.block_size || 15;
    cx = o[0] + (d[0] * s) / 2;
    cy = o[1] + (d[1] * s) / 2;
    cz = o[2] + (d[2] * s) / 2;
  }
  return {
    center: [cx, cy, cz],
    range: vc.range || 60,
    sill: vc.sill || 1,
    nugget: vc.nugget || 0,
    azimuth: vc.azimuth || 0,
    dip: vc.dip || 0,
    anisotropy: vc.anisotropy || 1,
  };
}
